export function formatNOK(amount: number | null | undefined): string {
  if (amount == null) return "—";
  return new Intl.NumberFormat('nb-NO', { style: 'currency', currency: 'NOK', maximumFractionDigits: 0 }).format(amount);
}

/**
 * Format en kroneverdi som "X,Y MNOK" (norsk komma som desimalskiller).
 * Eksempel: 12345678 → "12,3 MNOK", 0 → "0,0 MNOK".
 * For VIB-712 hero-KPIer på admin-dashboard.
 */
/** Kort kronebeløp for fliser og lister: MNOK fra én million, ellers hele kroner. */
export function formatKrCompact(amount: number | null | undefined): string {
  if (amount == null) return "—";
  return Math.abs(amount) >= 1_000_000 ? formatMNOK(amount) : formatNOK(amount);
}

export function formatMNOK(amount: number | null | undefined, decimals = 1): string {
  if (amount == null) return "—";
  const value = amount / 1_000_000;
  return new Intl.NumberFormat('nb-NO', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value) + ' MNOK';
}

export function formatPercent(value: number): string {
  return `${value}%`;
}

/**
 * Tall med norsk komma-desimal, uten enhet: 12,3.
 * Basis for prosent- og MNOK-visning i Måltall-widgetene.
 */
export function formatDecimalNb(value: number, decimals = 1): string {
  return value.toFixed(decimals).replace('.', ',');
}

/**
 * Prosent med norsk komma og mellomrom før tegnet: "74,5 %".
 * Designspråket for Måltall-widgetene (1 desimal default).
 */
export function formatPct(value: number, decimals = 1): string {
  return `${formatDecimalNb(value, decimals)} %`;
}

/**
 * Kroner → MNOK-tall UTEN suffiks: 12345678 → "12,3".
 * For widgets som styler "MNOK"-suffikset separat (mindre skrift).
 * Med suffiks: bruk formatMNOK.
 */
export function formatMnokValue(kr: number, decimals = 1): string {
  return formatDecimalNb(kr / 1_000_000, decimals);
}

/**
 * Akse-etikett i tusen-forkortelse: 0 → "0k", 300000 → "300k",
 * 1500000 → "1,5M". Brukes av prognose-widgetens y-akse.
 */
export function formatKShort(n: number): string {
  if (n === 0) return '0k';
  if (n >= 1_000_000) return `${formatDecimalNb(n / 1_000_000, 1)}M`;
  return `${Math.round(n / 1_000)}k`;
}

/**
 * Parser en RENT dato-only-streng ("YYYY-MM-DD") til en lokal `Date`
 * (midnatt i maskinens/testens tidssone) i stedet for `new Date(str)`, som
 * ECMAScript tolker som UTC-midnatt for date-only-strenger (§ Date Time
 * String Format). Uten dette skifter måneds-/dags-/årsattribusjon i
 * tidssoner vest for UTC (VIB-1339): `new Date("2026-05-01").getMonth()`
 * gir april, ikke mai, i America/New_York.
 *
 * Kun for RENE dato-strenger uten klokkeslett/tidssone-suffiks. Send aldri
 * en full ISO-timestamp inn hit — de skal parses med vanlig `new Date()`
 * (der UTC-tolkningen er korrekt, siden strengen selv bærer klokkeslettet).
 */
export function parseISODate(dateOnly: string): Date {
  const [y, m, d] = dateOnly.split("-").map(Number);
  return new Date(y, m - 1, d);
}

export function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function formatDateShort(date: string | Date | null | undefined): string {
  if (!date) return "—";
  const d = typeof date === "string" ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString('nb-NO', { day: '2-digit', month: 'short' });
}

/**
 * Kort dato med 2-sifret år: "06.04.26".
 * Brukes der det er trangt og året likevel relevant.
 */
export function formatDateCompact(date: string | Date | null | undefined): string {
  if (!date) return "—";
  const d = typeof date === "string" ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

/**
 * Kort dato + tid med år: "6. apr. 2026, 14:30".
 * Brukes til loggføringer, kommentartidsstempler osv. Året tas med så
 * tidsstempler ikke blir tvetydige på tvers av år.
 */
export function formatDateTimeShort(datetime: string | Date | null | undefined): string {
  if (!datetime) return "—";
  const d = typeof datetime === "string" ? new Date(datetime) : datetime;
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString('nb-NO', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/**
 * Relativ tid for «sist oppdatert»: "nå", "for 5 min siden", "for 3 timer siden",
 * "i går", ellers dato. Ugyldig eller manglende verdi gir "—".
 */
export function formatRelativeTime(datetime: string | Date | null | undefined, now: Date = new Date()): string {
  if (!datetime) return "—";
  const d = typeof datetime === "string" ? new Date(datetime) : datetime;
  if (Number.isNaN(d.getTime())) return "—";
  const minutes = Math.floor((now.getTime() - d.getTime()) / 60_000);
  if (minutes < 1) return "nå";
  if (minutes < 60) return `for ${minutes} min siden`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `for ${hours} ${hours === 1 ? "time" : "timer"} siden`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "i går";
  return formatDate(d.toISOString());
}

/**
 * Formaterer en timesats som "750 kr/t".
 * Bruker norsk currency-format uten desimaler og legger til "/t"-suffiks.
 */
export function formatHourlyRate(value: number | null | undefined): string {
  if (value == null) return "—";
  return `${new Intl.NumberFormat('nb-NO', { style: 'currency', currency: 'NOK', maximumFractionDigits: 0 }).format(value)}/t`;
}

/**
 * Fra–til-klokkeslett for en økt: "08:00–10:30". VIB-1201: felles
 * formatter for hover-tooltipen i budsjett-tabben (unngår en tredje lokal
 * duplikat — TaskEntriesRow.tsx og TimeTrackingPopup.tsx har hver sin egen
 * pre-eksisterende variant, ikke rørt her).
 */
export function formatTimeRange(startDate: string, durationSeconds: number): string {
  const start = new Date(startDate);
  const end = new Date(start.getTime() + durationSeconds * 1000);
  const hh = (d: Date) => String(d.getHours()).padStart(2, "0");
  const mm = (d: Date) => String(d.getMinutes()).padStart(2, "0");
  return `${hh(start)}:${mm(start)}–${hh(end)}:${mm(end)}`;
}

/** Månedsnavn med stor forbokstav, indeks 0 = januar. */
export const MONTH_NAMES_NB = [
  'Januar', 'Februar', 'Mars', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Desember',
] as const;

/** Hele timer med norsk tusenskille: 4852.6 → "4 853 t". For summer i Måltall. */
export function formatHoursWhole(hours: number): string {
  return `${new Intl.NumberFormat('nb-NO', { maximumFractionDigits: 0 }).format(hours)} t`;
}

export function formatHours(hours: number | null | undefined): string {
  // null/undefined = "ingen data" → fortsatt em-dash. 0 hours = "ingenting
  // logget" → "0t" så tabellkolonner blir lesbare istedenfor å se tomme ut.
  if (hours == null) return "—";
  if (hours === 0) return "0t";
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}t`;
  return `${h}t ${m}m`;
}

/**
 * Formaterer sekunder som "Xt Ym Zs" / "Ym Zs" / "Zs".
 * Brukes til live-timer i tidregistrering der vi vil vise sekund-presisjon.
 * Eksempler: 0 → "0s", 45 → "45s", 1800 → "30m 0s", 3725 → "1t 2m 5s".
 */
export function formatSeconds(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}t ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

/**
 * Formaterer en filstørrelse i bytes som menneskelig lesbar string.
 * Eksempler: 512 → "512 B", 2048 → "2.0 KB", 1572864 → "1.5 MB".
 */
export function formatFileSize(bytes: number | null | undefined): string {
  if (bytes == null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ── Smart time input parsing ───────────────────────────────

export type ParsedTime =
  | { type: "time"; value: string }
  | { type: "duration"; hours: number; minutes: number }
  | null;

/**
 * Parses flexible time input: "8" → 08:00, "1430" → 14:30,
 * "9.5" → 09:30, "2t" → duration, "1t30m" → duration.
 */
export function parseTimeInput(raw: string): ParsedTime {
  if (!raw) return null;
  const s = raw.trim().replace(",", ".");

  // Duration: "2t", "1.5t", "2t30m", "30m"
  const durMatch = s.match(/^(\d+(?:\.\d+)?)\s*t(?:\s*(\d+)\s*m)?$/i);
  if (durMatch) {
    return { type: "duration", hours: parseFloat(durMatch[1]), minutes: parseInt(durMatch[2] || "0") };
  }
  const mMatch = s.match(/^(\d+)\s*m$/i);
  if (mMatch) {
    return { type: "duration", hours: 0, minutes: parseInt(mMatch[1]) };
  }

  // Decimal hours: "9.5" → 09:30
  if (/^\d{1,2}\.\d+$/.test(s)) {
    const h = Math.floor(parseFloat(s));
    const m = Math.round((parseFloat(s) - h) * 60);
    if (h <= 23 && m <= 59) {
      return { type: "time", value: `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}` };
    }
  }

  // Pure digits: "8" → 08:00, "830" → 08:30, "1430" → 14:30
  if (/^\d{1,4}$/.test(s)) {
    if (s.length <= 2) {
      const h = parseInt(s);
      if (h >= 0 && h <= 23) return { type: "time", value: `${String(h).padStart(2, "0")}:00` };
    }
    if (s.length === 3) {
      const h = parseInt(s[0]);
      const m = parseInt(s.slice(1));
      if (h <= 23 && m <= 59) return { type: "time", value: `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}` };
    }
    if (s.length === 4) {
      const h = parseInt(s.slice(0, 2));
      const m = parseInt(s.slice(2));
      if (h <= 23 && m <= 59) return { type: "time", value: `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}` };
    }
  }

  // Already HH:MM
  if (/^\d{1,2}:\d{2}$/.test(s)) {
    const [h, m] = s.split(":").map(Number);
    if (h <= 23 && m <= 59) return { type: "time", value: `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}` };
  }

  return null;
}

/** Add minutes to a HH:MM string */
export function addMinutesToTime(time: string, mins: number): string {
  if (!time) return "";
  const [h, m] = time.split(":").map(Number);
  const total = h * 60 + m + mins;
  const nh = Math.floor(total / 60) % 24;
  const nm = total % 60;
  return `${String(nh).padStart(2, "0")}:${String(nm).padStart(2, "0")}`;
}

/**
 * Difference in minutes between two HH:MM strings. Pass `nextDay = true` when
 * the end time belongs to the following day (logging across midnight).
 */
export function timeDiffMinutes(from: string, to: string, nextDay = false): number {
  if (!from || !to) return 0;
  const [fh, fm] = from.split(":").map(Number);
  const [th, tm] = to.split(":").map(Number);
  let diff = (th * 60 + tm) - (fh * 60 + fm);
  // When the end time belongs to the following day (logging across midnight,
  // e.g. 22:00 -> 01:30), add a full day so the duration stays positive.
  if (nextDay) diff += 24 * 60;
  return diff;
}

const PHASE_KEYWORDS = new Set(["pre", "preprod", "prod", "production", "post", "postprod"]);

/**
 * Parses a task description into phase and task components.
 * Handles project names with " - " (e.g. "Europris - Bursdagsfilm - pre")
 * by scanning for known phase keywords instead of assuming fixed positions.
 */
export function parseTaskDescription(desc: string | null): { phase: string; task: string } {
  if (!desc) return { phase: "", task: "" };

  const parts = desc.split(" - ");

  // Scan parts for a known phase keyword (exact or in parentheses)
  for (let i = 0; i < parts.length; i++) {
    const trimmed = parts[i].trim().toLowerCase();

    // Exact match: "pre", "preprod", "production", etc.
    if (PHASE_KEYWORDS.has(trimmed)) {
      return {
        phase: parts[i].trim(),
        task: parts.slice(i + 1).join(" - ").trim(),
      };
    }

    // Phase in parentheses: "Bursdagsfilm (preprod)"
    const parenMatch = trimmed.match(/\((\w+)\)$/);
    if (parenMatch && PHASE_KEYWORDS.has(parenMatch[1])) {
      return {
        phase: parenMatch[1],
        task: parts.slice(i + 1).join(" - ").trim(),
      };
    }
  }

  // No phase keyword found — last part is the task, phase unknown
  if (parts.length >= 2) {
    return { phase: "", task: parts[parts.length - 1].trim() };
  }

  return { phase: "", task: desc };
}

/** App-satt invoice_type → norsk visningsetikett (VIB-1253). */
const APP_INVOICE_TYPE_LABELS: Record<string, string> = {
  startup: "Oppstart",
  interim: "Mellomfaktura",
  final: "Sluttfaktura",
  upsell: "Mersalg",
  credit_note: "Kreditnota",
};

/** PO VoucherType (poweroffice_status) → norsk visningsetikett (VIB-1253). */
const PO_VOUCHER_TYPE_LABELS: Record<string, string> = {
  OutgoingInvoice: "Faktura",
  OutgoingCreditNote: "Kreditnota",
};

/**
 * Fakturatype for visning: app-satt invoice_type har prioritet (dekker
 * fakturaer opprettet i Revehiet), ellers faller vi tilbake til PO sin
 * VoucherType (lagret i poweroffice_status) — dekker PO-synkede rader som
 * aldri har fått invoice_type satt (~96 % av radene før VIB-1253).
 */
export function formatInvoiceType(
  invoiceType: string | null | undefined,
  powerofficeStatus: string | null | undefined,
): string {
  if (invoiceType) return APP_INVOICE_TYPE_LABELS[invoiceType] ?? invoiceType;
  if (powerofficeStatus) return PO_VOUCHER_TYPE_LABELS[powerofficeStatus] ?? "-";
  return "-";
}

/**
 * VIB-2103: lagret datamengde for visning. Under 1 000 GB vises i GB,
 * over i TB med én desimal.
 *
 * 1 TB = 1 000 GB (desimalt), ikke 1 024. Det er det diskprodusentene
 * merker utstyret med, og dermed tallet den som teller opp arkivet leser
 * av — en binær omregning her ville gitt et annet tall enn hyllemerket.
 *
 * `null`, `undefined` og 0 gir «—»: et prosjekt lukket før VIB-1308 har
 * ingen registrert datamengde, og det er ikke det samme som null GB.
 */
export function formatStorage(gb: number | null | undefined): string {
  if (gb == null || gb <= 0) return "—";
  if (gb < 1000) return `${Math.round(gb).toLocaleString("nb-NO")} GB`;
  return `${(gb / 1000).toLocaleString("nb-NO", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} TB`;
}
